import React from "react";
import { NonDeletedExcalidrawElement } from "../element/types";
import { t } from "../i18n";

import { ExportOpts, BinaryFiles, UIAppState } from "../types";
import { Dialog } from "./Dialog";
import { LinkIcon } from "./icons";
import { ToolButton } from "./ToolButton";
import { actionSaveFileToDisk } from "../actions/actionExport";
import { Card } from "./Card";
import { FilledButton } from "./FilledButton";

import "./ExportDialog.scss";
import { nativeFileSystemSupported } from "../data/filesystem";
import { trackEvent } from "../analytics";
import { ActionManager } from "../actions/manager";
import { getFrame } from "../utils";

export type ExportCB = (
  elements: readonly NonDeletedExcalidrawElement[],
  scale?: number,
) => void;

const JSONExportModal = ({
  elements,
  appState,
  setAppState,
  files,
  actionManager,
  exportOpts,
  canExportImage,
  canvas,
  onCloseRequest,
}: {
  appState: UIAppState;
  setAppState: React.Component<any, UIAppState>["setState"];
  files: BinaryFiles;
  elements: readonly NonDeletedExcalidrawElement[];
  actionManager: ActionManager;
  onCloseRequest: () => void;
  exportOpts: ExportOpts;
  canExportImage: boolean;
  canvas: HTMLCanvasElement;
}) => {
  const { onExportToBackend } = exportOpts;
  const showBackupActions = canExportImage || exportOpts.saveFileToDisk;

  return (
    <div className="ExportDialog ExportDialog--json">
      <div className="ExportDialog-cards">
        {showBackupActions && (
          <div className="ExportDialog-actions">
            {canExportImage && (
              <div className="ExportDialog-actions__Action">
                <h4>{t("overwriteConfirm.action.exportToImage.title")}</h4>
                <div className="ExportDialog-actions__Action__content">
                  {t("overwriteConfirm.action.exportToImage.description")}
                </div>
                <FilledButton
                  variant="outlined"
                  color="muted"
                  label={t("overwriteConfirm.action.exportToImage.button")}
                  size="large"
                  fullWidth
                  // Deliberately does NOT force `exportEmbedScene`. In the
                  // overwrite-confirm dialog this button was a pre-overwrite
                  // backup, so embedding the scene was the point; here it is a
                  // plain export, and `exportEmbedScene` persists to
                  // localStorage - flipping it would silently fatten every
                  // later export with no way to tell why.
                  onClick={() => {
                    setAppState({ openDialog: { name: "imageExport" } });
                  }}
                />
              </div>
            )}
            {exportOpts.saveFileToDisk && (
              <div className="ExportDialog-actions__Action">
                <h4>{t("exportDialog.disk_title")}</h4>
                <div className="ExportDialog-actions__Action__content">
                  {t("exportDialog.disk_details")}
                  {!nativeFileSystemSupported &&
                    actionManager.renderAction("changeProjectName")}
                </div>
                <FilledButton
                  variant="outlined"
                  color="muted"
                  label={t("overwriteConfirm.action.saveToDisk.button")}
                  size="large"
                  fullWidth
                  onClick={() => {
                    actionManager.executeAction(actionSaveFileToDisk, "ui");
                  }}
                />
              </div>
            )}
          </div>
        )}
        {onExportToBackend && (
          <Card color="pink">
            <div className="Card-icon">{LinkIcon}</div>
            <h2>{t("exportDialog.link_title")}</h2>
            <div className="Card-details">{t("exportDialog.link_details")}</div>
            <ToolButton
              className="Card-button"
              type="button"
              title={t("exportDialog.link_button")}
              aria-label={t("exportDialog.link_button")}
              showAriaLabel={true}
              onClick={async () => {
                try {
                  trackEvent("export", "link", `ui (${getFrame()})`);
                  await onExportToBackend(elements, appState, files);
                  onCloseRequest();
                } catch (error: any) {
                  setAppState({ errorMessage: error.message });
                }
              }}
            />
          </Card>
        )}
        {exportOpts.renderCustomUI &&
          exportOpts.renderCustomUI(elements, appState, files, canvas)}
      </div>
    </div>
  );
};

export const JSONExportDialog = ({
  elements,
  appState,
  files,
  actionManager,
  exportOpts,
  canExportImage,
  canvas,
  setAppState,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: UIAppState;
  files: BinaryFiles;
  actionManager: ActionManager;
  exportOpts: ExportOpts;
  canExportImage: boolean;
  canvas: HTMLCanvasElement;
  setAppState: React.Component<any, UIAppState>["setState"];
}) => {
  const handleClose = React.useCallback(() => {
    setAppState({ openDialog: null });
  }, [setAppState]);

  return (
    <>
      {appState.openDialog?.name === "jsonExport" && (
        <Dialog
          onCloseRequest={handleClose}
          title={t("buttons.export")}
          size={916}
        >
          <JSONExportModal
            elements={elements}
            appState={appState}
            setAppState={setAppState}
            files={files}
            actionManager={actionManager}
            onCloseRequest={handleClose}
            exportOpts={exportOpts}
            canExportImage={canExportImage}
            canvas={canvas}
          />
        </Dialog>
      )}
    </>
  );
};
